import {Inject, inject, Injectable} from '@angular/core';
import {AppStorageService} from '../../../services/app-storage/app-storage.service';
import {AppStorageIdEnum} from '../../../enums/app-storage-id.enum';
import {AppStorage} from '../../../models/app-storage.model';
import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {StoreState} from '../models/store-state.model';
import {AkitaMimicService} from './akita-mimic.service';
import {UpdateStateCallback} from '../types/update-state-callback.type';
import {environment} from '../../../../environments/environment';


@Injectable({
    providedIn: 'root',
})
export class StoreService<StateType = StoreState> {
    protected appStore: StateType;
    protected appStoreName: string;
    protected appStorage: AppStorage;
    protected state: BehaviorSubject<Readonly<StateType>>;
    private readonly initialState: StateType;
    private readonly appStorageService = inject(AppStorageService);

    // eslint-disable-next-line @angular-eslint/prefer-inject
    public constructor(@Inject(String) appStoreName: string, @Inject(Object) initialState: StateType) {
        this.initialState = initialState;
        this.appStoreName = appStoreName;
        this.appStorage = this.appStorageService.getStorage(AppStorageIdEnum.AKITA_MIMIC);

        if (!this.appStorage.has(this.appStoreName)) {
            this.appStorage.set(this.appStoreName, initialState);
        }

        this.appStore = this.appStorage.get<StateType>(this.appStoreName) as StateType;
        this.state = new BehaviorSubject<Readonly<StateType>>(this.appStore);
        AkitaMimicService.registerStore(appStoreName, this as unknown as StoreService);
    }

    public update(state: Partial<StateType>): void;
    public update(stateCallback: UpdateStateCallback<StateType>): void;
    public update(stateOrCallback: Partial<StateType> | UpdateStateCallback<StateType>): void {
        let newState;
        const currentState: Readonly<StateType> = this.getValue();
        if (typeof stateOrCallback === 'function') {
            newState = stateOrCallback(currentState);
        } else {
            newState = stateOrCallback;
        }
        this.setState({ ...currentState, ...newState } as StateType);
    }

    public getValue(): StateType {
        return this.appStore;
    }

    public select<R>(selections: (store: StateType) => R): Observable<R> {
        return this.state.asObservable()
            .pipe(
                map(state => selections(state)),
                distinctUntilChanged()
            );
    }

    public reset(): void {
        this.setState(this.initialState);
    }

    private setState(newStateOrFnc: ((state: Readonly<StateType>) => StateType) | StateType): void {
        let appStore;
        if (typeof newStateOrFnc === 'function') {
            appStore =
                environment.stage !== 'dev'
                ? (newStateOrFnc as ((state: Readonly<StateType>) => StateType))(this.getValue())
                : this.deepFreeze((newStateOrFnc as ((state: Readonly<StateType>) => StateType))(this.getValue()));
        } else {
            appStore = newStateOrFnc;
        }
        this.appStore = appStore;
        this.appStorage.set(this.appStoreName, this.appStore);
        this.state.next(this.appStore);
    }

    private deepFreeze<R = object>(o: R): R {
        Object.freeze(o);

        const oIsFunction = typeof o === 'function';
        const hasOwnProp = Object.prototype.hasOwnProperty;

        Object.getOwnPropertyNames(o)
            .forEach(prop => {
                const attribute = o[prop as keyof R];
                if (
                    hasOwnProp.call(o, prop) &&
                    (oIsFunction ? prop !== 'caller' && prop !== 'callee' && prop !== 'arguments' : true) &&
                    attribute !== null &&
                    (typeof attribute === 'object' || typeof attribute === 'function') &&
                    !Object.isFrozen(attribute)
                ) {
                    this.deepFreeze(attribute);
                }
            });

        return o;
    }
}
